import useUpdateThread from "./hooks/useUpdateThread"
// eslint-disable-next-line no-restricted-imports
import { useBreakpointComponent } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { Form } from "@carrotfertility/carotene-core-x"
import React from "react"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import { InAppMessagingThread } from "./inAppMessagingTypes"
import useCreateThread from "./hooks/useCreateThread"
import useGetThreads from "./hooks/useGetThreads"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import { useIntl } from "react-intl"
import { Box } from "@carrotfertility/carotene-core"
import { FormTextFieldWithCharacterCount } from "#/services/common-forms/FormTextFieldWithCharacterCount"
import { FormSendIconButton, FormSendButton } from "./InAppMessagingFormButtons"

type MessageReplyContainerProps = {
  thread: InAppMessagingThread
  scrollThreadsListToTop: () => void
}

const SendButtonContainer = (): JSX.Element => {
  const sendButton = useBreakpointComponent([
    <FormSendIconButton key="smallest" />,
    <FormSendIconButton key="small" />,
    <FormSendButton key="med" />,
    <FormSendButton key="large" />
  ])

  return (
    <Box justifyContent="flex-end" width="auto" paddingY={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
      {sendButton}
    </Box>
  )
}

export function MessageReplyContainer({ thread, scrollThreadsListToTop }: MessageReplyContainerProps): JSX.Element {
  const { mutateAsync: updateThreadAsync } = useUpdateThread()
  const { mutateAsync: createThreadAsync } = useCreateThread()
  const { refetch: refetchThreads } = useGetThreads()
  const { setThreadIdForFailedUpdate, setThreadUpdateInFlight, setSelectedThread } = useInAppMessaging()
  const intl = useIntl()

  // this is a workaround to capture the div that surrounds the helper text message on the Carotene Textarea component
  const textAreaRef = React.useRef(null)

  // The second half of the duplicate-send guard; see `onSubmit`.
  const sendInFlight = React.useRef(false)

  // @ts-expect-error TS(7006) FIXME: Parameter 'values' implicitly has an 'any' type.
  async function send(values, instance): Promise<void> {
    setThreadUpdateInFlight(true)

    if (thread.isClosed) {
      values.viaFollowUpSourceId = thread.threadId
      // `subject` is OPTIONAL on the legacy thread and must never reach the care team as the raw
      // `Re: {subject}` placeholder — hence `?? ""`, which keeps that independent of how a given
      // react-intl version renders a missing value.
      //
      // Must stay `formatMessage` rather than a template literal: the "Re:" prefix is member-visible
      // copy and has to stay translatable.
      values.subject = intl
        .formatMessage({ defaultMessage: "Re: {subject}" }, { subject: thread.subject ?? "" })
        .substring(0, 150)

      try {
        const followupThread = await createThreadAsync(values)
        await refetchThreads()
        setSelectedThread(followupThread)
        setThreadIdForFailedUpdate(null)
        setThreadUpdateInFlight(false)
        scrollThreadsListToTop()
        instance.reset()
      } catch (error) {
        reportErrorMessage(`Unable to create follow up thread for thread id: ${thread.threadId}. Error: ${error}`)
        setThreadIdForFailedUpdate(thread.threadId)
        setThreadUpdateInFlight(false)
      }
    } else {
      values.threadId = thread.threadId

      try {
        await updateThreadAsync(values)
        instance.reset()
        setThreadIdForFailedUpdate(null)
        setThreadUpdateInFlight(false)
      } catch (error) {
        reportErrorMessage(`Unable to update or reply to thread id: ${thread.threadId}. Error: ${error}`)
        setThreadIdForFailedUpdate(thread.threadId)
        setThreadUpdateInFlight(false)
      }
    }
  }

  // SINGLE-FLIGHT. The submit control's disabled state only applies a render later, so without this
  // two submits dispatched in the same frame both post and leave a duplicate message in the member's
  // thread. Must be a ref, not state: the guard has to be readable synchronously. It is held from
  // before the first `await` until `send` settles, so a second submit is also rejected while the
  // request is still open.
  //
  // @ts-expect-error TS(7006) FIXME: Parameter 'values' implicitly has an 'any' type.
  async function onSubmit(values, instance): Promise<void> {
    if (sendInFlight.current) {
      return
    }
    sendInFlight.current = true
    try {
      await send(values, instance)
    } finally {
      // Both paths in `send` handle their own failures, so this only covers an unexpected throw —
      // but a stuck `true` would be permanent, and would silently kill every later reply.
      sendInFlight.current = false
    }
  }

  return (
    <Box>
      <Form onSubmit={onSubmit}>
        <Box
          className="input-group flex"
          justifyContent="space-between"
          marginInlineStart={(theme) => theme.spacing(theme.tokens.spacing.xl)}
          marginInlineEnd={(theme) => theme.spacing(theme.tokens.spacing.md)}
        >
          <Box width="100%" marginInlineEnd={(theme) => theme.spacing(theme.tokens.spacing.sm)}>
            <Box ref={textAreaRef} id="text-container">
              <FormTextFieldWithCharacterCount
                name="message"
                aria-label={intl.formatMessage({ defaultMessage: "Message" })}
                placeholder={intl.formatMessage({ defaultMessage: "Write a message..." })}
                required
                maxLength={1600}
                minRows={1}
                maxRows={9}
                rows={null} // TODO: OP-1041 will clean this up
                helperText={intl.formatMessage({ defaultMessage: "We usually respond within one business day." })}
              />
            </Box>
          </Box>
          <SendButtonContainer />
        </Box>
      </Form>
    </Box>
  )
}
